export const hasPermissions = (
  requiredPermissions = [],
  availablePermissions = []
) => {
  return requiredPermissions.some(permission =>
    availablePermissions.includes(permission)
  );
};

export const getCurrentAccount = ({ accounts } = {}, accountId = null) => {
  return accounts.find(account => Number(account.id) === Number(accountId));
};

export const getUserPermissions = (user, accountId) => {
  const currentAccount = getCurrentAccount(user, accountId) || {};
  return currentAccount.permissions || [];
};

export const getUserRole = (user, accountId) => {
  const currentAccount = getCurrentAccount(user, accountId) || {};
  if (currentAccount.custom_role_id) {
    return 'custom_role';
  }

  return currentAccount.role || 'agent';
};

/**
 * Filters and transforms items based on user permissions.
 *
 * @param {Object} items - An object containing items to be filtered.
 * @param {Array} userPermissions - Array of permissions the user has.
 * @param {Function} getPermissions - Function to extract required permissions from an item.
 * @param {Function} [transformItem] - Optional function to transform each item after filtering.
 * @returns {Array} Filtered and transformed items.
 */
export const filterItemsByPermission = (
  items,
  userPermissions,
  getPermissions,
  transformItem = (key, item) => ({ key, ...item })
) => {
  // Helper function to check if an item has the required permissions
  const hasRequiredPermissions = item => {
    const requiredPermissions = getPermissions(item);
    return (
      requiredPermissions.length === 0 ||
      hasPermissions(requiredPermissions, userPermissions)
    );
  };

  return Object.entries(items)
    .filter(([, item]) => hasRequiredPermissions(item)) // Keep only items with required permissions
    .map(([key, item]) => transformItem(key, item)); // Transform each remaining item
};

/**
 * Get menu permissions for a user in current account
 * @param {Object} user - The user object
 * @param {Number} accountId - The account ID
 * @returns {Object} Menu permissions hash { menuKey: true/false }
 */
export const getUserMenuPermissions = (user, accountId) => {
  const currentAccount = getCurrentAccount(user, accountId) || {};
  return currentAccount.menu_permissions || {};
};

/**
 * Get allowed menus array for a user
 * @param {Object} user - The user object
 * @param {Number} accountId - The account ID
 * @returns {Array<String>} Array of allowed menu keys
 */
export const getUserAllowedMenus = (user, accountId) => {
  const currentAccount = getCurrentAccount(user, accountId) || {};
  return currentAccount.allowed_menus || [];
};

/**
 * Check if user can access a specific menu
 * @param {String} menuKey - The menu key to check (e.g., 'captain', 'reports')
 * @param {Object} user - The user object
 * @param {Number} accountId - The account ID
 * @returns {Boolean} true if user can access the menu
 */
export const canAccessMenu = (menuKey, user, accountId) => {
  const currentAccount = getCurrentAccount(user, accountId) || {};

  // SuperAdmin has access to all menus
  if (user.type === 'SuperAdmin') {
    return true;
  }

  // Administrator has access to all menus
  if (currentAccount.role === 'administrator') {
    return true;
  }

  // Check allowed_menus array
  const allowedMenus = currentAccount.allowed_menus || [];
  return allowedMenus.includes(menuKey.toLowerCase());
};
